/**
 * UEM Hub Sidebar – Dark (#1e2532) with nested Devices > Directory (OUs) tree.
 * Folders and collections from tenant directory API (localStorage mirror for sync).
 */
import React, { useState, useEffect } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Laptop,
  Shield,
  Layers,
  Box,
  ShieldCheck,
  BarChart,
  Check,
  FileText,
  Settings,
  Server,
  FolderTree,
  Folder,
  FolderOpen,
  Terminal,
  Key,
  Users,
  Building2,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Activity,
  ArrowLeft,
  MessageSquare,
  MonitorSmartphone,
  Package,
  Lock,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  PLATFORM_NAV_ITEMS,
  PLATFORM_PAGE_ROLES,
  isPoliciesComplianceTab,
  NAV_MODULE_BY_ID,
  isPlatformModuleEnabled,
} from '../config/navigation';
import { STORAGE_FOLDERS, STORAGE_COLLECTIONS } from '../constants/storageKeys';
import {
  DIRECTORY_UPDATED_EVENT,
  DIRECTORY_ROOT_ID,
  UNASSIGNED_COLLECTION_ID,
  loadDirectory,
} from '../services/directoryStore';

interface OUNode {
  id: string;
  name: string;
  type?: 'folder' | 'collection';
  children?: OUNode[];
}

function dedupeById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();
  return items.filter((x) => {
    if (seen.has(x.id)) return false;
    seen.add(x.id);
    return true;
  });
}

function buildDirectoryTree(
  rawFolders: { id: string; name: string }[],
  rawCollections: { id: string; name: string; folderId?: string | null }[]
): OUNode[] {
  const folders = dedupeById(Array.isArray(rawFolders) ? rawFolders : []);
  const collections = dedupeById(Array.isArray(rawCollections) ? rawCollections : []);
  const collByFolder = new Map<string, { id: string; name: string }[]>();
  collections.forEach((c) => {
    const fid = c.folderId || 'folder-default';
    if (!collByFolder.has(fid)) collByFolder.set(fid, []);
    collByFolder.get(fid)!.push({ id: c.id, name: c.name });
  });
  const folderNodes = folders
    .filter((f) => f.id !== 'folder-default')
    .sort((a, b) => {
      if (a.id === 'folder-departments') return -1;
      if (b.id === 'folder-departments') return 1;
      if (a.id === 'folder-nutrinium') return -1;
      if (b.id === 'folder-nutrinium') return 1;
      return a.name.localeCompare(b.name);
    })
    .map((f) => ({
      id: f.id,
      name: f.name,
      type: 'folder' as const,
      children: (collByFolder.get(f.id) || []).sort((a, b) => a.name.localeCompare(b.name)),
    }));
  return [
    ...folderNodes,
    { id: UNASSIGNED_COLLECTION_ID, name: 'Unassigned', type: 'collection' as const, children: [] },
  ];
}

function loadDirectoryTreeFromMirror(): OUNode[] {
  try {
    let foldersJson = null as string | null;
    let collJson = null as string | null;
    try {
      const raw = localStorage.getItem('uem_user');
      const u = raw ? JSON.parse(raw) : null;
      const scope = String(u?.tenant_id || u?.tenant_code || '');
      if (scope) {
        foldersJson = localStorage.getItem(`${STORAGE_FOLDERS}:${scope}`);
        collJson = localStorage.getItem(`${STORAGE_COLLECTIONS}:${scope}`);
      }
    } catch {
      /* ignore */
    }
    if (!foldersJson) foldersJson = localStorage.getItem(STORAGE_FOLDERS);
    if (!collJson) collJson = localStorage.getItem(STORAGE_COLLECTIONS);
    const rawFolders = foldersJson ? JSON.parse(foldersJson) : [];
    const rawCollections = collJson ? JSON.parse(collJson) : [];
    return buildDirectoryTree(
      Array.isArray(rawFolders) ? rawFolders : [],
      Array.isArray(rawCollections) ? rawCollections : []
    );
  } catch {
    return [];
  }
}

interface UEMSidebarProps {
  tab: string;
  setTab: (id: string) => void;
  selectedOU: string | null;
  onSelectOU: (ouId: string | null) => void;
  viewingDirectory: boolean;
  onViewDirectory: (view: boolean) => void;
  resolveTabId: (id: string) => string;
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
}

const getIcon = (iconName: string, size = 20) => {
  const Icon =
    iconName === 'Laptop' ? Laptop :
    iconName === 'Shield' ? Shield :
    iconName === 'Layers' ? Layers :
    iconName === 'Box' ? Box :
    iconName === 'Terminal' ? Terminal :
    iconName === 'ShieldCheck' ? ShieldCheck :
    iconName === 'BarChart' ? BarChart :
    iconName === 'Check' ? Check :
    iconName === 'FileText' ? FileText :
    iconName === 'Settings' ? Settings :
    iconName === 'Server' ? Server :
    iconName === 'Key' ? Key :
    iconName === 'Users' ? Users :
    iconName === 'Building2' ? Building2 :
    iconName === 'Activity' ? Activity :
    iconName === 'MessageSquare' ? MessageSquare :
    iconName === 'MonitorSmartphone' ? MonitorSmartphone :
    iconName === 'Package' ? Package :
    null;
  if (Icon) return <Icon size={size} strokeWidth={2} />;
  return null;
};

const OTHER_NAV_ITEMS = [
  { id: 'overview', label: 'Overview', icon: 'Server' },
  { id: 'policies-compliance', label: 'Policies & Compliance', icon: 'Shield' },
  { id: 'app-deployment', label: 'Apps & Deployment', icon: 'Box' },
  { id: 'remote-execution', label: 'Remote Execution', icon: 'Terminal' },
  { id: 'patch-management', label: 'Patch Management', icon: 'ShieldCheck' },
  { id: 'inventory', label: 'Inventory', icon: 'BarChart' },
  { id: 'license-management', label: 'License Management', icon: 'Key' },
  { id: 'software-metering', label: 'Software Metering', icon: 'Activity' },
  { id: 'analytics', label: 'Analytics', icon: 'FileText' },
  { id: 'buntu-companion', label: 'Buntu Companion', icon: 'MonitorSmartphone' },
  { id: 'platform-admin', label: 'Platform Admin', icon: 'Building2' },
  { id: 'client-admin', label: 'Client Admin', icon: 'Users' },
  { id: 'settings', label: 'Settings', icon: 'Settings' },
];

export const UEMSidebar: React.FC<UEMSidebarProps> = ({
  tab,
  setTab,
  selectedOU,
  onSelectOU,
  viewingDirectory,
  onViewDirectory,
  resolveTabId,
  collapsed = false,
  onToggleCollapsed,
}) => {
  const { user, logout, access, isPlatformStaff, isPlatformAdmin, portalTenantId, portalTenantName, exitClientPortal } = useAuth();
  const role = String(user?.role || '').toUpperCase();
  const inCommandCenter = isPlatformStaff && !portalTenantId;
  const [devicesExpanded, setDevicesExpanded] = useState(resolveTabId(tab) === 'devices');
  const [directoryOUExpanded, setDirectoryOUExpanded] = useState(viewingDirectory);
  const [ouExpanded, setOUExpanded] = useState<Record<string, boolean>>({});
  const [directoryTree, setDirectoryTree] = useState<OUNode[]>(() => loadDirectoryTreeFromMirror());
  const [enabledModules, setEnabledModules] = useState<string[] | null>(access?.enabled_modules ?? null);

  // Show full main nav for every tenant user. Platform modules grant access;
  // unentitled modules still appear and open a No Access state in the main pane.
  const visibleNavItems = OTHER_NAV_ITEMS.filter((i) => {
    if (i.id === 'overview') return false;
    if (i.id === 'platform-admin') return false; // command center uses nested platform pages
    return true;
  });

  const visiblePlatformItems = PLATFORM_NAV_ITEMS.filter((i) => {
    const allowed = PLATFORM_PAGE_ROLES[i.id] || ['PLATFORM_ADMIN'];
    return allowed.includes(role) || isPlatformAdmin;
  });

  useEffect(() => {
    if (viewingDirectory) setDirectoryOUExpanded(true);
  }, [viewingDirectory]);

  useEffect(() => {
    if (selectedOU && directoryTree.length > 0) {
      for (const folder of directoryTree) {
        const hasCollection = folder.children?.some((c) => c.id === selectedOU);
        if (folder.id === selectedOU || hasCollection) {
          setOUExpanded((prev) => ({ ...prev, [folder.id]: true }));
          break;
        }
      }
    }
  }, [selectedOU, directoryTree]);

  useEffect(() => {
    let cancelled = false;
    loadDirectory()
      .then((dir) => {
        if (!cancelled) setDirectoryTree(buildDirectoryTree(dir.folders, dir.collections));
      })
      .catch(() => {
        if (!cancelled) setDirectoryTree(loadDirectoryTreeFromMirror());
      });
    // Load enabled modules for nav filtering (client roles)
    import('../services/api').then(({ apiService }) => {
      apiService.tenantMe()
        .then((me) => {
          if (!cancelled && Array.isArray(me?.modules)) setEnabledModules(me.modules);
        })
        .catch(() => {
          if (!cancelled) setEnabledModules([]);
        });
    });
    const onUpdate = () => setDirectoryTree(loadDirectoryTreeFromMirror());
    window.addEventListener(DIRECTORY_UPDATED_EVENT, onUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener(DIRECTORY_UPDATED_EVENT, onUpdate);
    };
  }, []);

  const isDevicesActive = resolveTabId(tab) === 'devices';

  const toggleOU = (id: string) => {
    setOUExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleDevicesClick = () => {
    setTab('devices');
    setDevicesExpanded(true);
    onViewDirectory(false);
    onSelectOU(null);
  };

  const handleDirectoryOUClick = () => {
    setTab('devices');
    setDevicesExpanded(true);
    setDirectoryOUExpanded(true);
    onViewDirectory(true);
    onSelectOU(DIRECTORY_ROOT_ID);
  };

  const handleOUClick = (ouId: string) => {
    setTab('devices');
    setDevicesExpanded(true);
    onViewDirectory(true);
    onSelectOU(ouId);
  };

  const renderOUTreeNode = (node: OUNode, depth: number) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = ouExpanded[node.id];
    const isFolder = node.type === 'folder' || (depth === 0 && node.id !== UNASSIGNED_COLLECTION_ID);
    return (
      <div key={node.id} style={{ marginLeft: depth * 12 }}>
        <div
          className={`flex items-center gap-1.5 py-1.5 px-2 rounded-md cursor-pointer hover:bg-white/10 transition-colors ${selectedOU === node.id ? 'bg-indigo-500/30' : ''}`}
          style={{ paddingLeft: 8 + depth * 12 }}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); hasChildren && toggleOU(node.id); }}
            className="p-0 border-0 bg-transparent cursor-pointer flex items-center justify-center w-5 h-5 text-slate-400 hover:text-white flex-shrink-0"
          >
            {hasChildren ? (
              isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />
            ) : (
              <span style={{ width: 14 }} />
            )}
          </button>
          {isFolder ? (
            hasChildren && isExpanded ? <FolderOpen size={14} className="text-amber-500 flex-shrink-0" /> : <Folder size={14} className="text-amber-500 flex-shrink-0" />
          ) : (
            <Layers size={14} className="text-indigo-400 flex-shrink-0" />
          )}
          <span
            className="text-sm text-slate-200 truncate flex-1 cursor-pointer"
            onClick={() => handleOUClick(node.id)}
          >
            {node.name}
          </span>
        </div>
        {hasChildren && isExpanded && node.children!.map((c) => renderOUTreeNode(c, depth + 1))}
      </div>
    );
  };

  return (
    <div className={`uem-sidebar${collapsed ? ' collapsed' : ''}`}>
      <div className="uem-sidebar-header">
        <div className="uem-sidebar-brand">
          <img
            src={`${process.env.PUBLIC_URL || ''}/Buntu.png`}
            alt="Buntu"
            className="uem-sidebar-logo-img"
          />
          {!collapsed && (
            <div className="uem-sidebar-brand-text">
              <div className="uem-sidebar-title">Buntu</div>
              <div className="uem-sidebar-subtitle">UEM Hub</div>
            </div>
          )}
        </div>
        {onToggleCollapsed && (
          <button
            type="button"
            className="uem-sidebar-toggle"
            onClick={onToggleCollapsed}
            title={collapsed ? 'Expand navigation' : 'Minimize navigation'}
            aria-label={collapsed ? 'Expand navigation' : 'Minimize navigation'}
          >
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>
        )}
      </div>

      <nav className="uem-sidebar-nav">
        {inCommandCenter ? (
          <>
            {!collapsed && (
              <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Platform Admin
              </div>
            )}
            {visiblePlatformItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setTab(item.id)}
                className={`uem-nav-item ${resolveTabId(tab) === item.id ? 'active' : ''}`}
                title={item.label}
              >
                {getIcon(item.icon)}
                {!collapsed && <span>{item.label}</span>}
              </button>
            ))}
          </>
        ) : (
          <>
            {isPlatformStaff && portalTenantId && (
              <button
                type="button"
                className="uem-nav-item"
                title="Back to Platform"
                onClick={() => {
                  exitClientPortal();
                  setTab('platform-dashboard');
                }}
              >
                <ArrowLeft size={18} />
                {!collapsed && <span>Back to Platform</span>}
              </button>
            )}
            {isPlatformStaff && portalTenantId && !collapsed && (
              <div className="px-3 py-1 text-[10px] text-indigo-300 truncate">
                Viewing: {portalTenantName || portalTenantId}
              </div>
            )}
        {/* Overview */}
        {(() => {
          const overviewLocked = !isPlatformModuleEnabled('overview', enabledModules);
          return (
            <button
              onClick={() => setTab('overview')}
              className={`uem-nav-item${resolveTabId(tab) === 'overview' ? ' active' : ''}${overviewLocked ? ' uem-nav-item--limited' : ''}`}
              title={overviewLocked ? 'Overview (No access — enable in Platform Admin)' : 'Overview'}
            >
              {getIcon('Server')}
              {!collapsed && <span>Overview</span>}
              {!collapsed && overviewLocked && <Lock size={14} className="uem-nav-lock" strokeWidth={2} />}
            </button>
          );
        })()}

        {/* Devices with nested Directory (OUs) */}
        <div className="uem-nav-devices-group">
          {(() => {
            const devicesLocked = !isPlatformModuleEnabled('devices', enabledModules);
            return (
          <button
            onClick={() => {
              if (collapsed) {
                handleDevicesClick();
                return;
              }
              setDevicesExpanded(!devicesExpanded);
              handleDevicesClick();
            }}
            className={`uem-nav-item uem-nav-parent${isDevicesActive ? ' active' : ''}${devicesLocked ? ' uem-nav-item--limited' : ''}`}
            title={devicesLocked ? 'Devices (No access — enable in Platform Admin)' : 'Devices'}
          >
            {!collapsed && (
              devicesExpanded ? <ChevronDown size={18} className="uem-chevron" /> : <ChevronRight size={18} className="uem-chevron" />
            )}
            {getIcon('Laptop')}
            {!collapsed && <span>Devices</span>}
            {!collapsed && devicesLocked && <Lock size={14} className="uem-nav-lock" strokeWidth={2} />}
          </button>
            );
          })()}
          {!collapsed && devicesExpanded && (
            <div className="uem-nav-children">
              <div
                className={`uem-nav-sub-header ${viewingDirectory && selectedOU === DIRECTORY_ROOT_ID ? 'bg-indigo-500/20 text-white' : ''}`}
                onClick={() => {
                  const willExpand = !directoryOUExpanded;
                  setDirectoryOUExpanded(willExpand);
                  if (willExpand) {
                    handleDirectoryOUClick();
                  } else {
                    onViewDirectory(false);
                    onSelectOU(null);
                  }
                }}
              >
                {directoryOUExpanded ? <ChevronDown size={14} className="uem-chevron" /> : <ChevronRight size={14} className="uem-chevron" />}
                <FolderTree size={16} className="text-indigo-400 flex-shrink-0" />
                <span>Directory (OUs)</span>
              </div>
              {directoryOUExpanded && (
                <div className="uem-ou-tree">
                  {directoryTree.length === 0 ? (
                    <p className="text-xs text-slate-500 px-2 py-2">No folders yet. Open Directory to create.</p>
                  ) : (
                    directoryTree.map((ou) => renderOUTreeNode(ou, 0))
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Asset Directory — top-level, next to Devices */}
        {(() => {
          const assetLocked = !isPlatformModuleEnabled('asset-management', enabledModules);
          const assetActive = resolveTabId(tab) === 'asset-management';
          return (
            <button
              onClick={() => {
                setTab('asset-management');
                window.history.pushState({}, '', '/assets/dashboard');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
              className={`uem-nav-item${assetActive ? ' active' : ''}${assetLocked ? ' uem-nav-item--limited' : ''}`}
              title={assetLocked ? 'Asset Directory (No access — enable in Platform Admin)' : 'Asset Directory'}
            >
              {getIcon('Package')}
              {!collapsed && <span>Asset Directory</span>}
              {!collapsed && assetLocked && <Lock size={14} className="uem-nav-lock" strokeWidth={2} />}
            </button>
          );
        })()}

        {/* Other nav items (Policies & Compliance is a single top-level item) */}
        {visibleNavItems.map((item) => {
          const entitled = isPlatformModuleEnabled(item.id, enabledModules);
          const locked = !!NAV_MODULE_BY_ID[item.id] && !entitled;
          const active =
            item.id === 'policies-compliance'
              ? isPoliciesComplianceTab(tab)
              : resolveTabId(tab) === item.id;
          return (
            <button
              key={item.id}
              onClick={() => {
                setTab(item.id);
                if (item.id === 'policies-compliance') {
                  window.history.pushState({}, '', '/policies/workstations');
                  window.dispatchEvent(new PopStateEvent('popstate'));
                }
              }}
              className={`uem-nav-item${active ? ' active' : ''}${locked ? ' uem-nav-item--limited' : ''}`}
              title={locked ? `${item.label} (No access — enable in Platform Admin)` : item.label}
            >
              {getIcon(item.icon)}
              {!collapsed && <span>{item.label}</span>}
              {!collapsed && locked && <Lock size={14} className="uem-nav-lock" strokeWidth={2} />}
            </button>
          );
        })}
          </>
        )}
      </nav>

      <div className="uem-sidebar-footer">
        <div className="uem-sidebar-divider" />
        <div className="uem-user-card">
          {!collapsed && (
            <div className="uem-user-info">
              <p className="uem-user-name">{user?.full_name || user?.username || 'User'}</p>
              <p className="uem-user-email">{user?.email || user?.username || ''}</p>
            </div>
          )}
          <button
            type="button"
            className={`uem-user-logout${collapsed ? ' icon-only' : ''}`}
            aria-label="Sign out"
            title="Sign out"
            onClick={logout}
          >
            <LogOut size={18} />
            {!collapsed && <span>Sign out</span>}
          </button>
        </div>
      </div>
    </div>
  );
};
